import {ProductDetailsSection_ArticleDetail} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Image,
  SPACING,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

const CARE_ICON_WIDTHS = [25, 50];
const CARE_ICON_BREAKPOINTS = [{columns: 12}];

interface ProductDetailsModalContentProps {
  articleDetails: ProductDetailsSection_ArticleDetail;
}

export const ProductDetailsModalContent: React.FC<
  ProductDetailsModalContentProps
> = ({articleDetails}) => {
  const {t} = useTranslation();
  const {lanes, defaultArticleNumber} = articleDetails;

  return (
    <ContentWrapper>
      {lanes.map((lane, index) => {
        const type = lane.type;
        if (!type) return null;

        switch (type.$case) {
          case 'materialLane':
            return (
              <Section key={index}>
                {lane.label && <SectionTitle>{lane.label}</SectionTitle>}
                <MaterialList>
                  {type.materialLane.bulletPoints.map((point, i) => (
                    <MaterialItem key={i}>{point}</MaterialItem>
                  ))}
                </MaterialList>
                {type.materialLane.careIcons.length > 0 && (
                  <CareIconList data-testid="product-details-care-icons">
                    {type.materialLane.careIcons.map((careIcon, i) => (
                      <CareIconItem key={careIcon.icon?.url ?? i}>
                        {careIcon.icon?.url && (
                          <Image
                            src={careIcon.icon.url}
                            ratio="1:1"
                            widths={CARE_ICON_WIDTHS}
                            breakpoints={CARE_ICON_BREAKPOINTS}
                            width="20"
                            height="20"
                            alt=""
                            role="presentation"
                            importance="low"
                            loading="lazy"
                          />
                        )}
                        <CareIconLabel>{careIcon.label}</CareIconLabel>
                      </CareIconItem>
                    ))}
                  </CareIconList>
                )}
              </Section>
            );

          case 'bulletPointLane':
            return (
              <Section key={index}>
                {lane.label && <SectionTitle>{lane.label}</SectionTitle>}
                <BulletList>
                  {type.bulletPointLane.bulletPoints.map((point, i) => (
                    <BulletItem key={i}>{point}</BulletItem>
                  ))}
                </BulletList>
              </Section>
            );

          case 'regularLane':
            return (
              <Section key={index}>
                {lane.label && <SectionTitle>{lane.label}</SectionTitle>}
                <MaterialList>
                  {type.regularLane.items.map((item, i) => (
                    <MaterialItem key={i}>{item}</MaterialItem>
                  ))}
                </MaterialList>
              </Section>
            );

          case 'sizeLane':
            return (
              <Section key={index}>
                {lane.label && <SectionTitle>{lane.label}</SectionTitle>}
                <BulletList>
                  {type.sizeLane.bulletPoints.map((point, i) => (
                    <BulletItem key={i}>{point}</BulletItem>
                  ))}
                </BulletList>
                {type.sizeLane.additionalText && (
                  <MaterialItem>{type.sizeLane.additionalText}</MaterialItem>
                )}
              </Section>
            );

          case 'manufacturerLane': {
            const manufacturerType = type.manufacturerLane.type;
            if (!manufacturerType) return null;

            return (
              <Section key={index}>
                {lane.label && <SectionTitle>{lane.label}</SectionTitle>}
                {manufacturerType.$case === 'manufacturerInfo' && (
                  <MaterialList>
                    <ManufacturerName>
                      {manufacturerType.manufacturerInfo.name}
                    </ManufacturerName>
                    {manufacturerType.manufacturerInfo.addressLines.map(
                      (line, i) => (
                        <MaterialItem key={`addr-${i}`}>{line}</MaterialItem>
                      ),
                    )}
                    {manufacturerType.manufacturerInfo.contacts.map(
                      (contact, i) => (
                        <MaterialItem key={`contact-${i}`}>
                          {contact}
                        </MaterialItem>
                      ),
                    )}
                  </MaterialList>
                )}
                {manufacturerType.$case === 'richText' && (
                  <RichTextContent
                    dangerouslySetInnerHTML={{
                      __html: manufacturerType.richText.text,
                    }}
                  />
                )}
              </Section>
            );
          }

          default:
            return null;
        }
      })}

      {defaultArticleNumber && (
        <ArticleNumber data-testid="product-details-article-number">
          {t('Item no.')} {defaultArticleNumber}
        </ArticleNumber>
      )}
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING['Y.Lg']};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Md']};
  align-self: stretch;
`;

const SectionTitle = styled.p`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS.Black};
  margin: 0;
`;

const MaterialList = styled.div`
  display: flex;
  flex-direction: column;
`;

const MaterialItem = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.4286;
  color: ${COLORS.Black};
  margin: 0;
`;

const CareIconList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  gap: ${SPACING['X.Md']};
  list-style: none;
`;

const CareIconItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACING['X.Md']};
`;

const CareIconLabel = styled.span`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.7143;
  color: ${COLORS.Black};
`;

const BulletList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: disc;
  padding-left: calc(${SPACING['X.Lg']} + ${SPACING['X.Sm']});
`;

const BulletItem = styled.li`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.4286;
  color: ${COLORS.Black};
`;

const ManufacturerName = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.4286;
  color: ${COLORS.Black};
  margin: 0;
`;

const RichTextContent = styled.div`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.4286;
  color: ${COLORS.Black};
  white-space: pre-line;

  a {
    text-decoration: none;
    pointer-events: none;
    color: ${COLORS.Black};
  }
`;

const ArticleNumber = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.4286;
  color: ${COLORS.Black};
  margin: 0;
  padding: 0 ${SPACING['X.Md']};
`;
